import { SiteProperties } from "@models/site";
import {
	Component,
	Event,
	EventEmitter,
	Prop,
	State,
	Watch,
	getAssetPath,
	h,
} from "@stencil/core";
import { fetchDocument } from "@utils/data";
import { getSiteTypeString } from "@utils/markers";

@Component({
	tag: "side-panel",
	styleUrl: "side-panel.scss",
	shadow: true,
})
export class SidePanel {
	/**
	 * The Sitenna API Key, this is required to use the component.
	 * This availble through the Sitenna Platform.
	 * If you have issues finding it, please contact support@sitenna.com
	 * @required
	 */
	@Prop() sitennaApiKey: string;
	/**
	 * Api Endpoint
	 * @required
	 */
	@Prop() apiEndpoint: string;
	/**
	 * Organization ID
	 * @required
	 */
	@Prop() organizationID: string;
	/**
	 * site properties to display
	 * @required
	 * @SiteProperties
	 */
	@Prop() site: SiteProperties;
	@Prop() appUrl: string;

	/**
	 * image src
	 */
	@State() imageSrc = "";

	@Event() closeSidebarEvent: EventEmitter<void>;
	@Event() showContactFormEvent: EventEmitter<void>;

	@Watch("site")
	sitesChangedHandler(newSite: SiteProperties, oldSite: SiteProperties) {
		if (newSite && newSite !== oldSite) {
			this.imageSrc = this.getEmptyStateIcon(newSite.type);
			this.getDocument();
		}
	}

	getEmptyStateIcon = (type: number) => {
		switch (type) {
			case 8:
				return getAssetPath("assets/street-asset-empty-state.png");
			case 14:
				return getAssetPath("assets/greenfield-empty-state.png");
			case 15:
				return getAssetPath("assets/rooftop-empty-state.png");
			default:
				return getAssetPath("assets/tower-empty-state.png");
		}
	};

	get address() {
		return `${this.site.address1 ?? ""} ${this.site.address2 ?? ""}`;
	}

	get elevation() {
		if (
			!this.site.elevation ||
			this.site.elevation === null ||
			this.site.elevation === 0
		)
			return "N/A";

		// check if elevation is a string
		if (typeof this.site.elevation === "string") {
			const elevation = parseFloat(this.site.elevation);
			if (Number.isNaN(elevation)) {
				return "N/A";
			}
		}

		return Math.round(this.site.elevation);
	}

	get height() {
		if (
			!this.site.height ||
			this.site.height === null ||
			this.site.height === 0
		)
			return "N/A";

		// check if height is a string
		if (typeof this.site.height === "string") {
			const height = parseFloat(this.site.height);
			if (Number.isNaN(height)) {
				return "N/A";
			}
		}

		return Math.round(this.site.height);
	}

	private sitennaRedirect = () => {
		// create query params from form data
		const params = new URLSearchParams({
			referrer: "site-navigator",
			orgID: this.organizationID,
			siteID: this.site.id,
		});
		let url = `${this.appUrl}/#/auth/signin?${params.toString()}`;
		// check if app url has a protocol
		if (!this.appUrl.includes("://")) {
			const scheme = window.location.protocol === "https:" ? "https" : "http";
			url = `${scheme}://${this.appUrl}/#/auth/signin?${params.toString()}`;
		}

		window.open(url, "_");
		this.closeSidebarEvent.emit();
	};

	getDocument = async () => {
		if (this.site?.documents && this.site.documents.length > 0) {
			const documentArr = JSON.parse(this.site.documents);
			// check if is an array
			if (Array.isArray(documentArr) && documentArr.length > 0) {
				// check if first element is a string
				if (typeof documentArr[0] === "string") {
					const docID = documentArr[0];

					fetchDocument(docID, this.sitennaApiKey, this.apiEndpoint)
						.then(async (resp) => {
							if (!resp.ok) {
								this.imageSrc = this.getEmptyStateIcon(this.site.type);
								console.error("Error fetching document");
								return;
							}

							const document = await resp.text();

							if (document && document !== null && document !== "") {
								this.imageSrc = document;
							} else {
								this.imageSrc = this.getEmptyStateIcon(this.site.type);
							}
						})
						.catch(() => {
							this.imageSrc = this.getEmptyStateIcon(this.site.type);
							console.error("Error fetching document");
						});
				}
			}
		}
	};

	render() {
		if (!this.site) {
			return null;
		}
		return (
			<section>
				{/* image section */}
				<location-image
					imagePath={this.imageSrc}
					imageAlt="placeholder"
					locationName={this.site.name}
					locationAddress={this.address}
				/>
				{/* text section */}
				<div class="text-section">
					<div class="bottom">
						<div class="text-group">
							<span class="key">Type</span>
							<span class="value">{getSiteTypeString(this.site.type)}</span>
						</div>
						<div class="text-group">
							<span class="key">Site Ref</span>
							<span class="value">{this.site.operator_site_id}</span>
						</div>
						<div class="text-group">
							<span class="key">Elevation (ft)</span>
							<span class="value">{this.elevation}</span>
						</div>
						<div class="text-group">
							<span class="key">Height (ft)</span>
							<span class="value">{this.height}</span>
						</div>
					</div>
				</div>
				{/* google map section */}
				<div class="map-section">
					<google-map
						position={{ lat: this.site.latitude, lng: this.site.longitude }}
						siteType={this.site.type}
					/>
				</div>
				{/* button section */}
				<div class="button-section">
					<button
						onClick={() => this.closeSidebarEvent.emit()}
						class="cancel-button"
						type="submit"
					>
						Close
					</button>
					<button
						onClick={() => this.showContactFormEvent.emit()}
						type="submit"
					>
						Register
					</button>
					<button onClick={this.sitennaRedirect} type="submit">
						Login
					</button>
				</div>
			</section>
		);
	}
}
